import { Grid, Typography } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { isOfficeModeEnabledForVisitorGroup, isQRInviteEnabledForVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import EditableCard from 'v2/components/EditableCard/EditableCard';
import InfoGrid from 'v2/components/InfoGrid/InfoGrid';
import { areQrCodeVisitsEnabled } from 'v2/utils/areQrCodeVisitsEnabled';
import {
  canEditVisitorGroup,
  getVisitorGroupOrigin,
  getVisitorGroupOriginLabel,
  VisitorGroupOrigin
} from 'v2/utils/visitorGroupOrigin';
import EditGroupDrawer from '../drawers/EditGroupDrawer';

interface GroupInfoProps {
  visitorGroup: VisitorGroup;
}

const getVisitorGroupOriginTooltipBody = (origin: VisitorGroupOrigin, translation: Translation) => {
  switch (origin) {
    case VisitorGroupOrigin.Atlas:
      return translation.group_origin_manual_hint;
    case VisitorGroupOrigin.Preset:
      return translation.group_is_a_preset_and_has_limited_editing_capabilities;
    case VisitorGroupOrigin.EntraId:
      return translation.group_origin_entra_tooltip;
    case VisitorGroupOrigin.Unknown:
      return undefined;
  }
};

const getVisitorGroupOriginTooltipContent = (origin: VisitorGroupOrigin, translation: Translation) => (
  <Grid container direction="row" rowSpacing={1}>
    <Typography variant="body2">{getVisitorGroupOriginTooltipBody(origin, translation)}</Typography>
  </Grid>
);

const GroupInfo: React.FC<GroupInfoProps> = ({ visitorGroup }) => {
  const { translation } = useTranslation();
  const { openDrawer } = useDrawerContext();

  const handleEditGroup = () => {
    openDrawer(<EditGroupDrawer visitorGroup={visitorGroup} />);
  };

  const canEdit = canEditVisitorGroup(visitorGroup);
  const origin = getVisitorGroupOrigin(visitorGroup);
  const qrCodeVisitsEnabled = areQrCodeVisitsEnabled();

  return (
    <EditableCard
      title={translation.group_info}
      editButtonId="editVisitorGroup"
      onEditClick={() => handleEditGroup()}
      spacing={2}
    >
      <InfoGrid>
        <InfoGrid.Label
          tooltip={
            origin !== VisitorGroupOrigin.Unknown ? getVisitorGroupOriginTooltipContent(origin, translation) : undefined
          }
        >
          {translation.origin}
        </InfoGrid.Label>
        <InfoGrid.Text>{upperFirst(getVisitorGroupOriginLabel(visitorGroup, translation))}</InfoGrid.Text>

        <InfoGrid.Label tooltip={translation.office_mode_permission_description}>
          {translation.office_mode}
        </InfoGrid.Label>
        <InfoGrid.Text>
          {isOfficeModeEnabledForVisitorGroup(visitorGroup) ? translation.allowed : translation.not_allowed}
        </InfoGrid.Text>

        {qrCodeVisitsEnabled && (
          <>
            <InfoGrid.Label tooltip={translation.qr_invites_permission_description}>
              {translation.qr_invites}
            </InfoGrid.Label>
            <InfoGrid.Text>
              {isQRInviteEnabledForVisitorGroup(visitorGroup) ? translation.allowed : translation.not_allowed}
            </InfoGrid.Text>
          </>
        )}

        {canEdit && visitorGroup.maxNumberOfMembers != null && (
          <>
            <InfoGrid.Label tooltip={translation.max_users_description}>{translation.max_users}</InfoGrid.Label>
            <Typography variant="body2" id="info-max-members">
              {visitorGroup.members.length}/{visitorGroup.maxNumberOfMembers}
              <Typography variant="caption" color="textSecondary" display="inline">
                {visitorGroup.members.length >= visitorGroup.maxNumberOfMembers
                  ? ` (${translation.amount_reached})`
                  : ''}
              </Typography>
            </Typography>
          </>
        )}
      </InfoGrid>
    </EditableCard>
  );
};

export default GroupInfo;
